import { z } from 'zod';
import { NextAction } from '../priority.js';
import { Id, InterventionStatus, Timestamp } from './common.js';

/**
 * Vague 7 : **module agenda** (facultatif, désactivé par défaut : module `agenda`). Aucune prise de
 * rendez-vous en ligne. Ressources : les **emplacements** typés (`LocationKind` : pont, poste), sans table
 * de plus. Les dossiers en cours se positionnent par un créneau facultatif (`AgendaSlot`) ; les
 * interventions planifiées gardent leur jour, leur créneau (`morning`, `afternoon`, `HH:MM`) et leur durée.
 */

/** Type d'emplacement : `area` (zone, parking : hors agenda), `lift` (pont), `bay` (poste de travail). */
export const LocationKind = z.enum(['area', 'lift', 'bay']);
export type LocationKind = z.infer<typeof LocationKind>;
export const LOCATION_KIND_LABELS: Record<LocationKind, string> = { area: 'Emplacement', lift: 'Pont', bay: 'Poste' };
/** Emplacements qui sont des colonnes de l'agenda « par ressource ». */
export const AGENDA_RESOURCE_KINDS: readonly LocationKind[] = ['lift', 'bay'];

/** Créneau d'un dossier en cours dans l'agenda : début (instant UTC) et durée (15 min à 7 jours). */
export const AgendaSlot = z.strictObject({ start: Timestamp, minutes: z.int().min(15).max(10_080) });
export type AgendaSlot = z.infer<typeof AgendaSlot>;

/** Période (jours locaux, bornes incluses, 31 jours au plus) et vue. */
export const AgendaQuery = z
  .strictObject({
    from: z.iso.date(),
    to: z.iso.date(),
    view: z.enum(['mechanic', 'resource']).default('mechanic'),
  })
  .refine((q) => q.from <= q.to, { message: 'Début après la fin', path: ['to'] })
  .refine((q) => (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000 <= 30, {
    message: '31 jours au plus',
    path: ['to'],
  });

export const AgendaColumn = z.object({
  /** Membre (`mechanic`) ou emplacement (`resource`) ; `null` pour la colonne « Non affecté ». */
  id: Id.nullable(),
  kind: z.enum(['mechanic', 'resource', 'unassigned']),
  name: z.string(),
});

export const AgendaItem = z.object({
  kind: z.enum(['planned', 'intervention']),
  /** Intervention planifiée ou dossier, selon `kind`. */
  id: Id,
  columnId: Id.nullable(),
  /** Jour local. */
  date: z.iso.date(),
  /** Heure locale `HH:MM` si connue (créneau horaire ou début du créneau d'agenda), sinon `null`. */
  start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable(),
  /** Planifiée : `morning` / `afternoon` si le créneau n'est pas une heure. */
  slot: z.enum(['morning', 'afternoon']).nullable(),
  minutes: z.int().nullable(),
  status: z.union([z.enum(['planned', 'checked_in', 'cancelled']), InterventionStatus]),
  vehicle: z.object({ id: Id, plate: z.string(), make: z.string(), model: z.string() }),
  customer: z.object({ id: Id, name: z.string() }),
  customerRequest: z.string(),
  /** `check_in` pour une planifiée (avec `intervention.create`), sinon l'action suivante du dossier. */
  nextAction: NextAction,
});
export type AgendaItem = z.infer<typeof AgendaItem>;

export const AgendaOutput = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  timezone: z.string(),
  view: z.enum(['mechanic', 'resource']),
  columns: z.array(AgendaColumn),
  /** Planifiées non annulées et dossiers non clôturés ayant un créneau dans la période. */
  items: z.array(AgendaItem),
});
export type AgendaOutput = z.infer<typeof AgendaOutput>;
